import { DateTime } from "luxon";

import type {
  BaseTranscriptTurn,
  SessionHighlight,
  TranscriptSummary,
} from "@app/common/types";

const DATE = DateTime.now();

const DEFAULT_SUMMARY_DATA = {
  editing: {
    isEdited: false,
    isSummaryEdited: false,
    isActionItemsEdited: false,
    isKeyQuestionsEdited: false,
    isTopicsEdited: false,
    isChaptersEdited: false,
  },
};

const DEFAULT_SUMMARY_ITEM_DATA = {
  isDeleted: false,
  isEdited: false,
};

const DEFAULT_CLIP_DATA = {
  startTime: DATE,
  endTime: DATE,
  relativeStartTime: 0,
  relativeEndTime: 0,
  thumbnailUrl: "",
  isEdited: false,
  isDeleted: false,
};

export const UPSELL_SUMMARY_CONTENT: TranscriptSummary = {
  ...DEFAULT_SUMMARY_DATA,
  summary: {
    ...DEFAULT_SUMMARY_ITEM_DATA,
    id: "summary",
    type: "summary",
    value:
      "Purus pulvinar mi augue parturient quisque quis habitant. Cursus ac quam odio a porta rhoncus at eros molestie. Tortor sagittis etiam diam vitae. Leo pellentesque et iaculis id augue commodo lectus venenatis egestas. Maecenas duis fermentum tortor congue ornare dignissim. Volutpat nibh elit posuere elementum neque.",
  },
  actionItems: [
    {
      ...DEFAULT_SUMMARY_ITEM_DATA,
      id: "ai_1",
      type: "action_item",
      value:
        "Fusce ultrices tellus sit amet nunc venenatis molestie. Maecenas et sapien augue. Donec ullamcorper congue justo sit amet sagittis",
    },
    {
      ...DEFAULT_SUMMARY_ITEM_DATA,
      id: "ai_2",
      type: "action_item",
      value: "Donec pellentesque ipsum nec imperdiet pharetra.",
    },
    {
      ...DEFAULT_SUMMARY_ITEM_DATA,
      id: "ai_3",
      type: "action_item",
      value: "Integer sed eros luctus, euismod arcu in, tempor nisi.",
    },
  ],
  keyQuestions: [
    {
      ...DEFAULT_SUMMARY_ITEM_DATA,
      id: "kq_1",
      type: "key_question",
      value: "Ut dictum eros et malesuada sagittis?",
    },
    {
      ...DEFAULT_SUMMARY_ITEM_DATA,
      id: "kq_2",
      type: "key_question",
      value: "Quisque mattis leo non gravida fermentum?",
    },
    {
      ...DEFAULT_SUMMARY_ITEM_DATA,
      id: "kq_3",
      type: "key_question",
      value: "Ut convallis mauris eu velit auctor, ut iaculis felis suscipit?",
    },
  ],
  topics: [],
  chapters: [
    {
      ...DEFAULT_CLIP_DATA,
      id: "c_1",
      title: "Phasellus sollicitudin felis eu sem malesuada rhoncus",
      description: "",
      topics: [
        {
          ...DEFAULT_SUMMARY_ITEM_DATA,
          id: "t_1",
          type: "topic",
          value: "Quisque rhoncus ex sed laoreet eleifend.",
        },
        {
          ...DEFAULT_SUMMARY_ITEM_DATA,
          id: "t_2",
          type: "topic",
          value:
            "Duis et felis convallis, euismod velit sit amet, venenatis velit.",
        },
      ],
      timestamp: "0:10",
    },
    {
      ...DEFAULT_CLIP_DATA,
      id: "c_2",
      title: "Proin placerat metus et fermentum ultrices.",
      description: "",
      topics: [],
      timestamp: "4:10",
    },
    {
      ...DEFAULT_CLIP_DATA,
      id: "c_3",
      title: "Etiam quis dictum nisi.",
      description: "",
      topics: [],
      timestamp: "15:00",
    },
    {
      ...DEFAULT_CLIP_DATA,
      id: "c_4",
      title: "Aenean purus nibh, congue eget rhoncus eget, venenatis et mi.",
      description: "",
      topics: [],
      timestamp: "23:30",
    },
  ],
};

const DEFAULT_TURN_DATA = {
  isDeleted: false,
  isEdited: false,
  relativeStartTime: {
    raw: 0,
    formatted: "0:00",
  },
  rawStartTime: DATE,
  rawEndTime: DATE,
  startTime: DATE,
  endTime: DATE,
  text: "",
  words: [],
};

const DEFAULT_PARAGRAPH_DATA = {
  isDeleted: false,
  rawStartTime: DATE,
  rawEndTime: DATE,
  startTime: DATE,
  endTime: DATE,
  words: [],
};

export const UPSELL_TRANSCRIPT_TURNS: BaseTranscriptTurn[] = [
  {
    ...DEFAULT_TURN_DATA,
    id: "turn_1",
    speaker: "Ronald K. Martinez",
    previousTurnId: undefined,
    paragraphs: [
      {
        ...DEFAULT_PARAGRAPH_DATA,
        id: "turn_1_paragraph_1",
        text: "Cras maximus sit amet diam id rhoncus. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Quisque pulvinar facilisis enim sit amet egestas.",
      },
    ],
  },
  {
    ...DEFAULT_TURN_DATA,
    id: "turn_2",
    speaker: "Sonia Cooper",
    previousTurnId: "turn_1",
    paragraphs: [
      {
        ...DEFAULT_PARAGRAPH_DATA,
        id: "turn_2_paragraph_1",
        text: "Etiam vel metus vestibulum, semper nulla vel, ultrices mi. Etiam in ullamcorper mi. Nullam id varius neque. Quisque efficitur interdum euismod.",
      },
    ],
  },
  {
    ...DEFAULT_TURN_DATA,
    id: "turn_3",
    speaker: "Marie Jackson",
    previousTurnId: "turn_2",
    paragraphs: [
      {
        ...DEFAULT_PARAGRAPH_DATA,
        id: "turn_3_paragraph_1",
        text: "Sed eget sapien ipsum.",
      },
    ],
  },
  {
    ...DEFAULT_TURN_DATA,
    id: "turn_4",
    speaker: "Sonia Cooper",
    previousTurnId: "turn_3",
    paragraphs: [
      {
        ...DEFAULT_PARAGRAPH_DATA,
        id: "turn_4_paragraph_1",
        text: "Quisque vehicula faucibus urna vitae egestas. Donec eleifend mauris vel fringilla tempus. Vestibulum dictum dictum eleifend. Sed blandit lacus porttitor risus mattis, nec dictum lorem tempus. Nullam consectetur justo eget eleifend efficitur. Sed sit amet lorem luctus, lobortis leo non, mattis lacus.",
      },
    ],
  },
];

export const UPSELL_HIGHLIGHTS: SessionHighlight[] = [
  {
    ...DEFAULT_CLIP_DATA,
    id: "h_1",
    description: "Increase in Engagement",
    type: "positive",
    quote:
      'Ronald said: "Sed laoreet dictum lorem, id tempus eros posuere non. Quisque diam..."',
    timestamp: "1:00",
  },
  {
    ...DEFAULT_CLIP_DATA,
    id: "h_2",
    description:
      "Proin metus augue, vestibulum et tincidunt vel, efficitur eget orci.",
    type: "topic",
    timestamp: "2:00",
  },
  {
    ...DEFAULT_CLIP_DATA,
    id: "h_3",
    description: "Increase in Engagement",
    type: "positive",
    quote: 'Sonia said: "Integer a diam blandit leo sagittis accumsan."',
    timestamp: "3:00",
  },
  {
    ...DEFAULT_CLIP_DATA,
    id: "h_4",
    description: "Sed ornare tortor eget urna lacinia vehicula?",
    type: "key_question",
    timestamp: "4:00",
  },
];
